import { useState, useEffect } from 'react';
import ContestCard, { type ContestCardProps } from './ContestCard';
import { formatTimeRemaining } from '../../lib/dateFormat';

/**
 * Hook to get live countdown time for a contest
 * Updates every second for contests less than 24 hours away
 */
const useLiveCountdown = (startTimeUtc?: string): string => {
  const [timeDisplay, setTimeDisplay] = useState<string>('TBD');

  useEffect(() => {
    if (!startTimeUtc) {
      setTimeDisplay('TBD');
      return;
    }

    const startDate = new Date(startTimeUtc);
    const now = new Date();
    const diffMs = startDate.getTime() - now.getTime();

    // Only set up interval if contest is less than 24 hours away and hasn't started
    if (diffMs > 0 && diffMs < 24 * 60 * 60 * 1000) {
      // Initial calculation
      setTimeDisplay(formatTimeRemaining(startDate));

      // Update every second
      const interval = setInterval(() => {
        const currentTime = new Date();
        const currentDiff = startDate.getTime() - currentTime.getTime();

        if (currentDiff <= 0) {
          // Contest has started
          setTimeDisplay('Live');
          clearInterval(interval);
        } else {
          setTimeDisplay(formatTimeRemaining(startDate));
        }
      }, 1000);

      return () => clearInterval(interval);
    } else {
      // For contests >= 24 hours away, show relative time. If already started, show Live.
      if (diffMs <= 0) {
        setTimeDisplay('Live');
      } else {
        setTimeDisplay(formatTimeRemaining(startDate));
      }
    }
  }, [startTimeUtc]);

  return timeDisplay;
};

/**
 * ContestCard wrapper that provides live countdown for start time
 */
export default function ContestCardWithCountdown(props: ContestCardProps) {
  // If the contest is in-play, always show "Live"
  const contestIsLive = props.status === 'live' || props.isLive === true;
  const liveStartTime = contestIsLive ? 'Live' : useLiveCountdown(props.startDateTime);

  return <ContestCard {...props} startTime={liveStartTime} />;
}
